/**
 * Core-100 R4 wave 3 (CORE-C / CORE-F Rel) — a DENIED Passport section read is
 * not an EMPTY one.
 *
 * Row-level security answers a read it does not permit with zero rows, which
 * the browser cannot tell from "this person has no records". The Passport's
 * derived sections (Time Signature / minute productivity; the Skill Heatmap's
 * proficiency, diagnostic, readiness and job-statistics sources) read tables
 * whose policies differ per source, so a viewer the server does not let read a
 * source used to be told the PERSON had "no time data" / "no evidence".
 *
 * `passport_section_read_access` (migration 20270206024100; SQL proof
 * core_cf_w3_section_read_access, which asserts each flag equals the
 * principal's own RLS read for six principal classes) states which sources the
 * signed-in caller's read basis covers. It is SECURITY INVOKER: it can never
 * claim more than the caller's own reads would return.
 *
 * Failure semantics: if the access read itself fails, the caller gets a thrown
 * error (the section reports a failure), never a guessed permission.
 */
import { supabase } from '@/integrations/supabase/client';

export type PassportSectionSource = 'proficiency' | 'diagnostic' | 'readiness' | 'job_stats' | 'time';

export interface PassportSectionReadAccess {
  /** The caller may open this Passport at all (technician_profiles RLS). */
  visible: boolean;
  proficiency: boolean;
  diagnostic: boolean;
  readiness: boolean;
  job_stats: boolean;
  time: boolean;
}

/** Thrown by a section read whose source the caller's read basis does not cover. */
export class SectionReadNotPermittedError extends Error {
  readonly source: PassportSectionSource;
  constructor(source: PassportSectionSource) {
    super(`This Passport section is not visible to you (${source}).`);
    this.name = 'SectionReadNotPermittedError';
    this.source = source;
  }
}

export function isSectionReadNotPermitted(error: unknown): error is SectionReadNotPermittedError {
  return error instanceof SectionReadNotPermittedError
    || (typeof error === 'object' && error !== null
        && (error as { name?: unknown }).name === 'SectionReadNotPermittedError');
}

const SOURCES: readonly (keyof PassportSectionReadAccess)[] = [
  'visible', 'proficiency', 'diagnostic', 'readiness', 'job_stats', 'time',
];

/**
 * Read the caller's access to one Passport's derived-section sources.
 * Throws on a failed read and on a malformed answer (a missing flag is not a
 * "no" and not a "yes" — it is a read we cannot interpret).
 */
export async function fetchPassportSectionReadAccess(
  technicianProfileId: string,
  shopTechnicianId?: string | null,
): Promise<PassportSectionReadAccess> {
  const { data, error } = await supabase.rpc('passport_section_read_access' as never, {
    p_technician_profile_id: technicianProfileId,
    p_shop_technician_id: shopTechnicianId ?? null,
  } as never);
  if (error) throw error;
  const raw = (data ?? null) as Record<string, unknown> | null;
  if (!raw || typeof raw !== 'object') {
    throw new Error('passport_section_read_access returned no answer');
  }
  const out = {} as PassportSectionReadAccess;
  for (const key of SOURCES) {
    if (typeof raw[key] !== 'boolean') {
      throw new Error(`passport_section_read_access returned no '${key}' flag`);
    }
    out[key] = raw[key] as boolean;
  }
  return out;
}
